// @ts-strict-ignore
import { useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import fileDownload from 'js-file-download'
import { debounce } from 'lodash-es'
import { logSentryError } from '@node-space/utils'
import { rootInitialState } from 'components/FilterBar/context/reducer'
import { useFilterDateValues } from 'components/FilterBar/hooks/useFilterDateValues'
import useFilterMerchant from 'components/FilterBar/hooks/useFilterMerchant'
import useFilterStatus from 'components/FilterBar/hooks/useFilterStatus'
import useFilterTag from 'components/FilterBar/hooks/useFilterTag'
import { usePaymentsQuery } from 'hooks/queries'
import { getAllPaymentsCSV } from 'services/PaymentsService'
import { PaymentList } from 'types/payments'
import { QueryMetaType } from 'types/reactQuery'

const usePaymentsApi = () => {
  const [isExportingCSV, setIsExportingCSV] = useState(false)
  const [isSearchPending, setIsSearchPending] = useState(false)
  dayjs.extend(utc)

  const {
    initialDateValue,
    safeFromDate,
    safeToDate,
    formattedStartDate,
    formattedEndDate,
    dateValue,
    setDateValue,
    onDateSelected,
    resetDateRange,
  } = useFilterDateValues()
  const { status, setStatus, resetStatus } = useFilterStatus()
  const { tag, setTag, resetTag } = useFilterTag()
  const { merchant, setMerchant, resetMerchant } = useFilterMerchant()

  const [searchByRef, setSearchByRef] = useState('')
  const [searchByTransactionHash, setSearchByTransactionHash] = useState('')
  const [searchByUUID, setSearchByUUID] = useState('')

  const [paymentsList, setPaymentsList] = useState<PaymentList>({
    enteredTo: new Date(Date.now()),
    visible: false,
    isTransactionsLoaded: false,
    transactions: [],
    paging: { offset: 0, total: 0, max: 20 },
    pagingMax: 20,
    selectedPayloadOpen: false,
  })

  const { paging, pagingMax } = paymentsList

  const meta: QueryMetaType = {
    onSuccess: res => processPayments(res),
    errorAdditionalData: {
      merchant,
      status,
      searchByRef,
      searchByTransactionHash,
      searchByUUID,
    },
  }

  const { refetch, isFetching, isError, error } = usePaymentsQuery(
    {
      paging,
      from: dayjs(safeFromDate)
        .hour(dayjs().hour())
        .minute(dayjs().minute())
        .second(dayjs().second())
        .utc()
        .format('YYYY-MM-DD'),
      to: dayjs(safeToDate)
        .hour(dayjs().hour())
        .minute(dayjs().minute())
        .second(dayjs().second())
        .utc()
        .format('YYYY-MM-DD'),
      merchant,
      status,
      searchByRef,
      searchByTransactionHash,
      searchByUUID,
      tag,
    },
    {
      meta,
    }
  )

  const processPayments = data => {
    setIsSearchPending(false)
    const transactions = data?.results || []
    setPaymentsList(prev => ({
      ...prev,
      isTransactionsLoaded: true,
      transactions,
      paging: {
        ...prev?.paging,
        total: data?.paging?.total || 0,
        offset: data?.paging?.offset || 0,
      },
    }))
  }

  const refetchRef = useRef(refetch)
  useEffect(() => {
    refetchRef.current = refetch
  }, [refetch])

  const debouncedRefetch = useCallback(
    debounce(() => {
      refetchRef.current()
    }, 500),
    []
  )

  useEffect(() => {
    if (searchByRef || searchByTransactionHash || searchByUUID) {
      setIsSearchPending(true)
      setPaymentsList(prev => ({
        ...prev,
        transactions: [],
        paging: { ...prev.paging, offset: 0 },
      }))
      debouncedRefetch()
    } else {
      refetch()
    }

    return () => {
      debouncedRefetch.cancel()
    }
  }, [searchByRef, searchByTransactionHash, searchByUUID])

  useEffect(() => {
    const isDefaultDates =
      dateValue?.from?.toDateString() === rootInitialState.from?.toDateString() &&
      dateValue?.to?.toDateString() === rootInitialState?.to?.toDateString()

    if (isDefaultDates) {
      refetch()
    }
  }, [dateValue?.from, dateValue?.to])

  useEffect(() => {
    if (
      dateValue?.from?.getTime() === new Date(formattedStartDate)?.getTime() &&
      dateValue?.to?.getTime() === new Date(formattedEndDate)?.getTime() &&
      status === rootInitialState?.status &&
      merchant === rootInitialState?.merchant &&
      tag === rootInitialState?.tag
    ) {
      refetch()
    }
  }, [status, merchant, tag])

  useEffect(() => {
    refetch()
  }, [paging.offset])

  useEffect(() => {
    return () => {
      resetDateRange()
      resetStatus()
      resetTag()
      resetMerchant()
    }
  }, [])

  const handleExportClicked = useCallback(async () => {
    setIsExportingCSV(true)

    try {
      const res = await getAllPaymentsCSV(
        dateValue?.from ? dateValue.from.toISOString() : '',
        dateValue?.to ? dateValue.to.toISOString() : '',
        status,
        searchByRef,
        merchant,
        searchByTransactionHash,
        searchByUUID
      )
      const data = (res as any)?.data

      const searchTerm = searchByRef || searchByTransactionHash || searchByUUID

      try {
        fileDownload(
          data,
          `payment-${dateValue?.from}-${dateValue?.to}${searchTerm ? `-${searchTerm}` : ''}.csv`,
          'application/octet-stream'
        )
      } catch (err) {
        logSentryError('Error from usePaymentsApi - handleExportClicked - try fileDownload', err, {
          merchant,
          dateValue,
        })
      }
      setIsExportingCSV(false)
    } catch (err) {
      logSentryError(
        'Error from usePaymentsApi - handleExportClicked - try getAllPaymentsCSV',
        err,
        {
          merchant,
          dateValue,
        }
      )
      setIsExportingCSV(false)
    }
  }, [
    dateValue?.from,
    dateValue?.to,
    status,
    searchByRef,
    searchByTransactionHash,
    searchByUUID,
    merchant,
  ])

  const onSearchByRefChanged = (search: string) => setSearchByRef(search)
  const onSearchByTransactionHash = (search: string) => {
    setSearchByTransactionHash(search)
  }
  const onSearchByUUID = (search: string) => setSearchByUUID(search)
  const onPaymentStatusChanged = status => setStatus(status)
  const onPaymentTagChange = tag => setTag(tag)

  const onMerchantSelected = useCallback(
    merchant => {
      setMerchant(merchant)
    },
    [dateValue?.from, dateValue?.to, status, merchant, searchByRef, paging, pagingMax, tag]
  )

  const onPageChange = useCallback(
    ({ paging: { offset } }) => {
      setPaymentsList(prev => ({
        ...prev,
        paging: {
          ...paging,
          offset: offset,
          max: pagingMax,
        },
      }))
    },
    [dateValue?.from, dateValue?.to, status, merchant, searchByRef, paging, pagingMax, tag]
  )

  const onResetAll = () => {
    setDateValue({
      from: new Date(formattedStartDate),
      to: new Date(formattedEndDate),
    })
    setStatus(rootInitialState?.status)
    setMerchant(rootInitialState?.merchant)
    setTag(rootInitialState?.tag)
    setSearchByRef('')
    resetSearch()

    resetDateRange()
  }

  const resetSearch = () => {
    setSearchByRef('')
    setSearchByTransactionHash('')
    setSearchByUUID('')
  }

  return {
    executor: refetch,
    isLoading: isFetching || isSearchPending,
    isError,
    error,
    data: {
      paymentsList,
      dateValue,
      initialDateValue,
      merchant,
      searchByRef,
      searchByTransactionHash,
      searchByUUID,
      status,
      isExportingCSV,
      tag,
    },
    actions: {
      onDateSelected,
      handleExportClicked,
      onSearchByRefChanged,
      onSearchByTransactionHash,
      onSearchByUUID,
      onPaymentStatusChanged,
      onPaymentTagChange,
      onMerchantSelected,
      onPageChange,
      onResetAll,
      setPaymentsList,
      showSearchResult: () => {
        setPaymentsList(prev => ({
          ...prev,
          transactions: [],
          paging: { ...prev.paging, offset: 0 },
        }))
        refetch()
      },
      resetSearch,
    },
  }
}

export default usePaymentsApi
